import React from 'react';
import { normalizeArray } from './utils/normalize';

const ConsultingProfessionalResumePreview = ({ componentRef, resumeData }) => {
  // Check if we should show user data or template default data
  const isUploadedResume = resumeData && Object.keys(resumeData).length > 0 && !resumeData.isTemplate;
  const shouldShowUserData = resumeData && (
    !resumeData.isTemplate || 
    resumeData.full_name || 
    resumeData.email || 
    resumeData.phone ||
    (resumeData.experience && resumeData.experience.length > 0) ||
    (resumeData.education && resumeData.education.length > 0) ||
    (resumeData.certifications && resumeData.certifications.length > 0)
  );

  // Helper function to get name
  const getName = () => {
    if (resumeData.full_name) return resumeData.full_name;
    if (resumeData.first_name && resumeData.last_name) {
      return `${resumeData.last_name}, ${resumeData.first_name}`;
    }
    return "Last Name, First Name";
  };

  // Helper function to build role line
  const getRoleLine = () => {
    const jobTitle = resumeData.job_title || "";
    const location = resumeData.location || "";
    
    // Only return formatted string if both job title and location exist
    if (jobTitle && location) {
      return `${jobTitle}, ${location}`;
    } else if (jobTitle) {
      return jobTitle;
    } else if (location) {
      return location;
    }
    return "";
  };

  // Helper function to get full experience details with location and dates
  const getExperienceDetails = () => {
    // Prefer relevant_experience as it has description_points
    const experiences = resumeData.relevant_experience && resumeData.relevant_experience.length > 0 
      ? resumeData.relevant_experience 
      : (resumeData.work_experience || resumeData.experience || []);
      
    if (experiences && experiences.length > 0) {
      const mapped = experiences.map((exp) => {
        const bulletSource =
          (Array.isArray(exp.description_points) && exp.description_points.length
            ? exp.description_points
            : null) ||
          (Array.isArray(exp.duties) && exp.duties.length ? exp.duties : null) ||
          (Array.isArray(exp.responsibilities) && exp.responsibilities.length
            ? exp.responsibilities
            : null) ||
          exp.summary ||
          exp.description ||
          [];
        const bullets = normalizeArray(bulletSource);
        return {
          title: exp.position || exp.title || "",
          company: exp.company || "",
          location: exp.location || "",
          startDate: exp.start_date || exp.start_year || "",
          endDate: exp.end_date || exp.end_year || "",
          bullets,
        };
      });
      if (typeof window !== "undefined") {
        console.log("[ConsultingPreview] experienceDetails", {
          total: mapped.length,
          items: mapped,
        });
      }
      return mapped;
    }
    
    // Default template data
    return [
      {
        title: "For a health insurance client",
        company: "Senior Consultant",
        location: "New York, USA",
        startDate: "Jan 2020",
        endDate: "Present",
        bullets: [
          "Led a team of 20+ members supporting performance and compensation review cycles across 51 countries and 11 languages for a Fortune 100 client in sports apparel.",
          "Applied design thinking to recommend continuous service improvements, incident resolution, and defect management.",
          "Managed localization, global and concurrent employment scenarios in SAP HCM."
        ]
      },
      {
        title: "For a telecom client",
        company: "Business Analyst",
        location: "San Francisco, USA",
        startDate: "Jun 2017",
        endDate: "Dec 2019",
        bullets: [
          "Designed and developed SAP Sweden payroll module for one-off tax processing.",
          "Enhanced SAP standard reports to support Concurrent Employment requirements in Sweden public sector."
        ]
      }
    ];
  };

  // Helper function to get education and certifications
  const getEducationAndCertifications = () => {
    const items = [];
    
    // Add education
    if (resumeData.education && resumeData.education.length > 0) {
      resumeData.education.forEach(edu => {
        const parts = [];
        const {
          degree,
          qualification,
          course,
          institution,
          institute,
          location,
          start_year,
          start_date,
          end_year,
          end_date,
          graduation_date,
        } = edu || {};

        const detailsParts = [
          degree || qualification || course || "",
          institution || institute || "",
          location || "",
        ].filter(Boolean);

        const start =
          start_date || start_year || (edu?.date && edu.date.split("-")?.[0]) || "";
        const end =
          end_date ||
          end_year ||
          graduation_date ||
          (edu?.date && edu.date.split("-")?.[1]) ||
          "";

        if (start || end) {
          const range = [start, end].filter(Boolean).join(" - ");
          if (range) {
            detailsParts.push(range);
          }
        }

        if (detailsParts.length > 0) {
          items.push(detailsParts.join(" • "));
        }
      });
    } else if (!shouldShowUserData) {
      // Only show default template data if it's the template (not uploaded resume)
      items.push('B.S. Computer Science');
      items.push('Engineering, University Name');
    }
    
    // Add certifications
    if (resumeData.certifications && resumeData.certifications.length > 0) {
      resumeData.certifications.forEach(cert => {
        if (typeof cert === 'string') {
          items.push(cert);
        } else if (cert && cert.name) {
          const details = [
            cert.name,
            cert.provider,
            cert.year || cert.date || cert.issued_on || cert.issued || "",
          ].filter(Boolean);
          items.push(details.join(" • ") || cert.name);
        }
      });
    } else if (!shouldShowUserData) {
      // Only show default template data if it's the template (not uploaded resume)
      items.push('Agile CSM • 2023');
      items.push('AZ900 • 2023');
    }
    
    return items;
  };

  const isTemplateResume = resumeData?.isTemplate !== false;
  const trimText = (value) => (typeof value === 'string' ? value.trim() : '');
  const pickDisplay = (value, fallback = '') => {
    const trimmed = trimText(value);
    if (trimmed) return trimmed;
    return isTemplateResume ? fallback : '';
  };
  const defaultSummaryText = "Person Name is a B.S. Computer Science graduate with 5+ years of hands-on software engineering experience in energy industry. Person Name has an educational background in Computer Science, Statistics and a strong business acumen. This combination allows him / her to use the technical skills to support and drive business outcomes.";
  
  // Helper function to split summary into paragraphs
  const getSummaryParagraphs = () => {
    const summary = pickDisplay(resumeData.professional_summary, defaultSummaryText);
    
    // Split into 2-3 paragraphs
    const sentences = summary.split('. ');
    const paragraphs = [];
    let currentParagraph = '';
    
    sentences.forEach((sentence, index) => {
      if (sentence.trim()) {
        currentParagraph += sentence + (sentence.endsWith('.') ? '' : '.');
        
        // Create paragraph every 2-3 sentences or at the end
        if ((index + 1) % 2 === 0 || index === sentences.length - 1) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
      }
    });
    
    return paragraphs.length > 0 ? paragraphs : [summary];
  };

  const name = getName();
  const roleLine = getRoleLine();
  const experienceDetails = getExperienceDetails();
  const educationAndCerts = getEducationAndCertifications();
  const summaryParagraphs = getSummaryParagraphs();
  const dedupeSkills = (items = []) =>
    Array.from(new Set(items.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const combinedTechnicalSkills = dedupeSkills([
    ...(Array.isArray(resumeData.key_skills) ? resumeData.key_skills : []),
    ...(Array.isArray(resumeData.industry_expertise) ? resumeData.industry_expertise : []),
    ...(Array.isArray(resumeData.functional_expertise) ? resumeData.functional_expertise : []),
    ...(Array.isArray(resumeData.programming_languages) ? resumeData.programming_languages : []),
    ...(Array.isArray(resumeData.databases) ? resumeData.databases : []),
    ...(Array.isArray(resumeData.data_analytics_tools) ? resumeData.data_analytics_tools : []),
    ...(Array.isArray(resumeData.data_science_tools) ? resumeData.data_science_tools : []),
    ...(Array.isArray(resumeData.big_data_platforms) ? resumeData.big_data_platforms : []),
  ]);

  return (
    <div className="a4-page" ref={componentRef}>
      {/* HEADER (Full Width) */}
      <header className="a4-header">
        <h1 className="hdr-name">{name}</h1>
        <div className="hdr-role">{roleLine}</div>
        {/* Contact Information */}
        <div className="hdr-contact">
          {resumeData.email && <span>Email: {resumeData.email}</span>}
          {resumeData.phone && (
            <span>{resumeData.email ? ' | ' : ''}Phone: {resumeData.phone}</span>
          )}
          {resumeData.location && (
            <span>
              {(resumeData.email || resumeData.phone) ? ' | ' : ''}
              Location: {resumeData.location}
            </span>
          )}
        </div>
        <div className="hdr-contact">
          {resumeData.linkedin && (
            <span>LinkedIn: {resumeData.linkedin}</span>
          )}
          {resumeData.github && (
            <span>
              {resumeData.linkedin ? ' | ' : ''}
              GitHub: {resumeData.github}
            </span>
          )}
          {resumeData.website && (
            <span>
              {(resumeData.linkedin || resumeData.github) ? ' | ' : ''}
              Website: {resumeData.website}
            </span>
          )}
        </div>
        <div className="hdr-rule" />
      </header>

      {/* 3-COLUMN GRID LAYOUT */}
      <div className="a4-grid">
        {/* LEFT PANEL: Photo + Summary */}
        <div className="col-left section">
          <div className="headshot">
            {resumeData.profile_image_url ? (
              <img 
                src={resumeData.profile_image_url} 
                alt="Profile" 
                style={{
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover', 
                  borderRadius: '50%'
                }} 
              />
            ) : (
              <div>
                <div style={{fontSize: '24px'}}>📷</div>
                <div className="headshot-text">Change headshot</div>
              </div>
            )}
          </div>
          <div className="left-summary">
            {summaryParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>

        {/* MIDDLE COLUMN: Experience Highlights */}
        <div className="col-middle section">
          <h2 className="sec-title">Experience Highlights</h2>
          <div className="highlights">
            {experienceDetails.map((exp, i) => (
              <div key={i} className="experience-item">
                <div className="experience-header">
                  <div className="experience-title">{exp.title}</div>
                  <div className="experience-meta">
                    <span className="experience-company">{exp.company}</span>
                    <span className="experience-location">{exp.location}</span>
                    <span className="experience-dates">{exp.startDate} - {exp.endDate}</span>
                  </div>
                </div>
                {exp.bullets && exp.bullets.length > 0 && (
                  <ul className="experience-bullets">
                    {exp.bullets.map((bullet, j) => (
                      <li key={j}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Projects Section - Middle Column */}
          {resumeData.projects && resumeData.projects.length > 0 && (
            <>
              <h2 className="sec-title" style={{ marginTop: '25px' }}>Projects</h2>
              {resumeData.projects.map((project, i) => (
                <div key={i} style={{ marginBottom: '20px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>
                    {project.title || project.name || `Project ${i + 1}`}
                  </div>
                  {project.tech_stack && (
                    <div style={{ fontSize: '10px', marginBottom: '5px', color: '#666', fontStyle: 'italic' }}>
                      Tech Stack: {Array.isArray(project.tech_stack) ? project.tech_stack.join(', ') : project.tech_stack}
                    </div>
                  )}
                  {project.description && (
                    <div style={{ fontSize: '11px', color: '#ffffff', lineHeight: '1.4' }}>
                      {Array.isArray(project.description) 
                        ? project.description.join('. ') 
                        : project.description}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* RIGHT COLUMN: Skills Rail */}
        <div className="col-right section">
          <h2 className="sec-title">Skills</h2>

          {/* Skills */}
          {combinedTechnicalSkills.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Technical Skills</div>
              <ul className="rail-list">
                {combinedTechnicalSkills.map((skill, i) => (
                  <li key={i}>{skill}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Education & Certification */}
          {educationAndCerts.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Education & Certification</div>
              <ul className="rail-list">
                {educationAndCerts.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Interests */}
          {resumeData.interests && resumeData.interests.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Interests</div>
              <ul className="rail-list">
                {resumeData.interests.map((interest, i) => (
                  <li key={i}>{interest}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Publications */}
          {resumeData.publications && resumeData.publications.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Publications</div>
              <ul className="rail-list">
                {resumeData.publications.map((pub, i) => (
                  <li key={i}>
                    {pub.title || pub.name || `Publication ${i + 1}`}
                    {pub.year && ` (${pub.year})`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Volunteer Work */}
          {resumeData.volunteer_work && resumeData.volunteer_work.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Volunteer Work</div>
              <ul className="rail-list">
                {resumeData.volunteer_work.map((volunteer, i) => (
                  <li key={i}>
                    {volunteer.title || volunteer.position || `Volunteer ${i + 1}`}
                    {volunteer.organization && ` - ${volunteer.organization}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Info */}
          {resumeData.additional_info && resumeData.additional_info.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Additional Info</div>
              <ul className="rail-list">
                {resumeData.additional_info.map((info, i) => (
                  <li key={i}>{info}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Languages */}
          {resumeData.languages && resumeData.languages.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Languages</div>
              <ul className="rail-list">
                {resumeData.languages.map((lang, i) => (
                  <li key={i}>
                    {lang.language || lang}
                    {lang.proficiency && ` (${lang.proficiency})`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Achievements */}
          {resumeData.achievements && resumeData.achievements.length > 0 && (
            <div className="rail-group">
              <div className="rail-title">Achievements</div>
              <ul className="rail-list">
                {resumeData.achievements.map((achievement, i) => (
                  <li key={i}>{achievement}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Infosys Logo */}
          <div style={{
            textAlign: 'right', 
            marginTop: '20px', 
            fontSize: '12px', 
            color: '#FFFFFF'
          }}>
            Infosys®
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultingProfessionalResumePreview;